import { useState } from 'react';
import React from 'react';
import { Timeline as MuiTimeline, TimelineContent, TimelineItem, TimelineSeparator } from '@mui/lab';
import { Divider } from '@mui/material';
import { Box, Button, CircularProgress, Fade, Grid, Paper, Stack, SvgIcon, Typography } from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';
import { format } from 'date-fns';
import { replaceNewLineWithBreak } from '../../utils';
import {
  DefaultAdditionalEventProperties,
  Event,
  TimelineDailyEventsItemProps,
  TimelineEventItemProps,
  TimelineLoadMoreItemProps,
  TimelineProps,
} from './Timeline.types';
import {
  DATE_HEADER_FORMAT,
  DATE_KEY_FORMAT,
  DATE_TIME_FORMAT,
  DEFAULT_ICON_MAP,
  DEFAULT_SOURCE_ICON_MAP,
  DEFAULT_SOURCE_LABEL_MAP,
  convertTimestamp,
  defaultSourceIconRender,
  getSourceLabel,
} from './Timeline.utils';
import {
  defaultGetDerivedSeverity,
  defaultGetTimestamp,
  defaultIconRender,
  defaultRenderEventContent,
  defaultRenderEventTitle,
} from './Timeline.utils';
import { TimelineSkeleton } from './TimelineSkeleton';

const TimelineHeader = ({ date }: { date: Date }) => (
  <TimestampTypography
    data-testid="separator"
    variant="caption"
    color={'grey.700'}
    tsFormat={DATE_HEADER_FORMAT}
    date={date}
  />
);

const TimelineEventItemTitle = ({
  children,
  color,
  renderIcons,
}: {
  children: React.ReactNode;
  color: string;
  renderIcons: () => React.ReactNode;
}) => {
  const icon = renderIcons();
  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        alignItems: 'center',
      }}
      mb={1}
    >
      {icon && (
        <SvgIcon sx={{ fontSize: '12px' }} color={color}>
          {icon}
        </SvgIcon>
      )}
      <Typography color={`${color}.main`} variant="overline" fontWeight={600}>
        {children}
      </Typography>
    </Stack>
  );
};

const TimelineEventItemContent = ({ children }) => {
  return (
    <Typography
      variant="body2"
      sx={{
        color: 'text.primary',
      }}
      dangerouslySetInnerHTML={{ __html: replaceNewLineWithBreak(children) }}
    />
  );
};

const TimelineEventSource = ({
  children,
  renderIcons,
}: {
  children: React.ReactNode;
  renderIcons: () => React.ReactNode;
}) => {
  const icon = renderIcons();
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
      }}
    >
      {icon}
      <Typography variant="overline" color="grey.600">
        {children}
      </Typography>
    </Stack>
  );
};

const TimestampTypography = ({
  color = 'grey.500',
  date,
  tsFormat,
  variant = 'caption',
  ...props
}: {
  color?: string;
  date: Date | string | number;
  tsFormat: string;
  variant?: Variant;
  props?: Record<string, unknown>;
}) => {
  return (
    <Typography
      variant={variant}
      sx={{
        color,
      }}
      {...props}
    >
      {format(date, tsFormat)}
    </Typography>
  );
};

const TimelineEventItem = <T extends object & { date: Date }>({
  event,
  iconMapping,
  renderIcons,
  renderEventContent,
  renderEventTitle,
  handleSelectEvent,
  getDerivedSeverity,
  timelineItemProps,
  enableElevation = false,
  sourceIconMapping,
  renderSourceIcons,
  sourceLabelMapping,
}: TimelineEventItemProps<T>) => {
  const { date } = event;
  const [elevation, setElevation] = useState(0);
  const derivedSeverity = getDerivedSeverity?.(event);
  const sourceLabel = getSourceLabel({ event, sourceLabelMapping });
  const hasSourceLabel = !!sourceLabel?.toString().trim();

  const handleClickEvent = () => {
    handleSelectEvent?.(event);
  };

  return (
    <Fade in>
      <TimelineItem
        onClick={handleClickEvent}
        data-testid={`event-${derivedSeverity}`}
        data-event-type={event.type}
        sx={{
          cursor: enableElevation ? 'pointer' : 'default',
          '&:before': {
            display: 'none',
          },
          mx: -2,
          ...timelineItemProps.sx,
        }}
      >
        <TimelineContent>
          <Paper
            elevation={elevation}
            onMouseEnter={() => enableElevation && setElevation(2)}
            onMouseLeave={() => enableElevation && setElevation(0)}
            {...timelineItemProps.content}
            sx={{
              p: 1.5,
              borderLeft: '2px',
              borderLeftStyle: 'solid',
              borderLeftColor: `${derivedSeverity}.main`,
              background: (theme) =>
                theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.lightGrey,
              transition: (theme) =>
                theme.transitions.create('all', {
                  duration: theme.transitions.duration.shortest,
                }),
              ...(timelineItemProps.content?.sx ?? {}),
            }}
          >
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
              <TimelineEventItemTitle color={derivedSeverity} renderIcons={() => renderIcons(iconMapping, event)}>
                {renderEventTitle(event)}
              </TimelineEventItemTitle>
              <TimestampTypography variant="caption" color={'grey.500'} tsFormat={DATE_TIME_FORMAT} date={date} />
            </Stack>
            <Box mb={1}>
              <TimelineEventItemContent>{renderEventContent && renderEventContent(event)}</TimelineEventItemContent>
            </Box>
            <Divider sx={{ width: '100%', mb: 1 }} />
            {hasSourceLabel && (
              <TimelineEventSource renderIcons={() => renderSourceIcons(sourceIconMapping, event)}>
                {sourceLabel}
              </TimelineEventSource>
            )}
            <Typography variant="caption" color="grey.600" sx={{ whiteSpace: 'pre-line' }}>
              {event?.user ?? '-'}
            </Typography>
          </Paper>
        </TimelineContent>
      </TimelineItem>
    </Fade>
  );
};

const TimelineLoadMoreItem = ({
  onLoadMore,
  loadMoreLabel = 'Show more',
  isLoadingMore,
}: TimelineLoadMoreItemProps) => (
  <TimelineItem
    sx={{
      width: '100%',
      '&:before': {
        display: 'none',
      },
    }}
  >
    <TimelineContent>
      <Grid container alignItems="center" justifyContent="center">
        {isLoadingMore ? (
          <CircularProgress color="secondary" size={24} />
        ) : (
          <Button
            onClick={onLoadMore}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              textDecoration: 'underline',
            }}
            variant="text"
          >
            {loadMoreLabel}
          </Button>
        )}
      </Grid>
    </TimelineContent>
  </TimelineItem>
);

const TimelineDailyEventsItem = <T extends object & { date: Date }>({
  day,
  dailyEvents,
  iconMapping,
  renderIcons,
  renderEventContent,
  renderEventTitle,
  handleSelectEvent,
  getDerivedSeverity,
  selectedEventId,
  timelineItemProps,
  enableElevation,
  sourceIconMapping,
  renderSourceIcons,
  sourceLabelMapping,
}: TimelineDailyEventsItemProps<T>) => {
  return (
    <Stack width="100%">
      <TimelineSeparator>
        <TimelineHeader date={day as Date} />
      </TimelineSeparator>

      {dailyEvents.map((event) => (
        <TimelineEventItem
          key={event.uuid}
          event={event}
          iconMapping={iconMapping}
          renderIcons={renderIcons}
          renderEventContent={renderEventContent}
          renderEventTitle={renderEventTitle}
          handleSelectEvent={handleSelectEvent}
          getDerivedSeverity={getDerivedSeverity}
          selectedEventId={selectedEventId}
          timelineItemProps={timelineItemProps}
          enableElevation={enableElevation}
          sourceIconMapping={sourceIconMapping}
          renderSourceIcons={renderSourceIcons}
          sourceLabelMapping={sourceLabelMapping}
        />
      ))}
    </Stack>
  );
};

export const Timeline = <T extends object = DefaultAdditionalEventProperties>({
  events,
  iconMapping = DEFAULT_ICON_MAP,
  sourceIconMapping = DEFAULT_SOURCE_ICON_MAP,
  sourceLabelMapping = DEFAULT_SOURCE_LABEL_MAP,
  selectedEventId = null,
  isLoadingMore,
  isLoading,
  loadMoreLabel,
  hasLoadMoreButton = true,
  noEventsLabel = 'No recorded events',
  slotProps,
  enableElevation,
  reversedOrder = false,
  renderIcons = defaultIconRender,
  renderEventContent = defaultRenderEventContent,
  renderEventTitle = defaultRenderEventTitle,
  getTimestamp = defaultGetTimestamp,
  getDerivedSeverity = defaultGetDerivedSeverity,
  onLoadMore,
  onSelectEvent,
  renderSourceIcons = defaultSourceIconRender,
}: TimelineProps<T>) => {
  const { timelineProps = {}, timelineItemProps = {} } = slotProps ?? {};

  if (isLoading) {
    return <TimelineSkeleton />;
  }

  // Sort events by date
  const sortedEvents = events
    .map((e) => ({ ...e, date: new Date(convertTimestamp(getTimestamp(e))) }))
    .sort(
      (a, b) =>
        reversedOrder
          ? a.date.getTime() - b.date.getTime() // oldest first
          : b.date.getTime() - a.date.getTime() // newest first
    );

  // Group events by day
  const eventsByDay = sortedEvents.reduce<Record<string, Event<T & { date: Date }>[]>>((acc, event) => {
    const day = format(event.date, DATE_KEY_FORMAT);
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(event as Event<T & { date: Date }>);
    return acc;
  }, {});

  const handleSelectEvent = (event: Event<T>) => {
    if (onSelectEvent) {
      onSelectEvent(event);
    }
  };

  return (
    <MuiTimeline position="right" sx={{ width: '100%', px: 0, mt: 0, ...timelineProps.sx }}>
      {!events || !events.length ? (
        <Typography variant="body1" sx={{ color: 'text.secondary' }} p={2}>
          {noEventsLabel}
        </Typography>
      ) : (
        <>
          {Object.entries(eventsByDay).map(([day, dailyEvents], index) => (
            <TimelineDailyEventsItem
              key={`${index} - ${day}`}
              day={day}
              dailyEvents={dailyEvents}
              selectedEventId={selectedEventId}
              iconMapping={iconMapping}
              renderIcons={renderIcons}
              renderEventContent={renderEventContent}
              renderEventTitle={renderEventTitle}
              handleSelectEvent={handleSelectEvent}
              timelineItemProps={timelineItemProps}
              enableElevation={enableElevation}
              getDerivedSeverity={getDerivedSeverity}
              sourceIconMapping={sourceIconMapping}
              renderSourceIcons={renderSourceIcons}
              sourceLabelMapping={sourceLabelMapping}
            />
          ))}

          {typeof onLoadMore === 'function' && hasLoadMoreButton && (
            <TimelineLoadMoreItem onLoadMore={onLoadMore} isLoadingMore={isLoadingMore} loadMoreLabel={loadMoreLabel} />
          )}
        </>
      )}
    </MuiTimeline>
  );
};
